// TODO: декомпозировать, отрефакторить

import { SCHEME2NUMBER_MAP } from '@@/schemes/maps/scheme2number.map';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { useStore } from '@lib/state-engine';
import { warehouseModel } from '@entities/warehouse';
import { PageTemplate } from '@ui/templates';
import { useResponseErrorInterceptorHandler } from '@@/common/hooks/use-response-error-interceptor-handler.hook';
import Draggable from 'react-draggable';
import { ButtonWithIcon } from '@ui/button';
import { StepList } from '@widgets/create-warehouse/step-list';
import {
  Coordinate,
  CoordinateObject,
  CreateMap,
  DispositionSchema,
  modes,
} from '@widgets/create-warehouse/map';
import { WarehouseName } from '@widgets/create-warehouse/warehouse-name';
import { WarehouseSize } from '@widgets/create-warehouse/warehouse-size';
// import { TiltAngle } from '@widgets/create-warehouse/tilt-angle';
import { InputOutput } from '@widgets/create-warehouse/input-output';
import { ISelectTypeProps, SelectType } from '@widgets/create-warehouse/select-type';
import { WarehouseCoordinates } from '@widgets/create-warehouse/warehouse-coordinates';
import { showInformer } from '@ui/status-informer';
import { DefaultWarehouseStep, FixedWarehouseStep } from '@pages/create-warehouse/ts';
import {
  DEFAULT_WAREHOUSE_STEPS,
  FIXED_WAREHOUSE_STEP,
} from './constants/warehouse-steps.constants';
import { WarehouseTypes, IWarehouseAdjustment } from '@@/warehouses/ts';
import {
  WAREHOUSES_ID_QUERY_NAME,
  WAREHOUSES_TYPE_QUERY_NAME,
} from '@@/warehouses/config/query.config';
import { Scheme } from '@@/schemes/ts';
import { CrossIcon } from '@@/common/icons/cross.icon';
import { DiContainer } from '@@/di/di-container';
import { WarehousesService } from '@@/warehouses/services/warehouses.service';
import { ClientStorageService } from '@@/common/services/client-storage.service';

import styles from './styles.module.css';

const warehousesService = DiContainer.get(WarehousesService);
const clientStorageService = DiContainer.get(ClientStorageService);

const VIEWER_SETTINGS = 'VIEWER_SETTINGS';

export function CreateWarehousePage({ onCreate }: CreateWarehousePageProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const isFixedWarehouse = location.pathname === '/storage/create-warehouse/fixed';

  const steps = useMemo(
    () => (isFixedWarehouse ? FIXED_WAREHOUSE_STEP : DEFAULT_WAREHOUSE_STEPS),
    [location],
  );
  const [currentStep, setCurrentStep] = useState<
    DefaultWarehouseStep | FixedWarehouseStep
  >(DefaultWarehouseStep.Name);
  const [availableSteps, setAvailableSteps] = useState<
    Array<DefaultWarehouseStep | FixedWarehouseStep>
  >([]);

  const [mode, setMode] = useState<modes>('none');
  const [square, setSquare] = useState(0);
  const [topAngle, setTopAngle] = useState<Coordinate | undefined>(undefined);
  const [bottomAngle, setBottomAngle] = useState<Coordinate | undefined>(undefined);
  const [input, setInput] = useState<CoordinateObject | undefined>(undefined);
  const [output, setOutput] = useState<CoordinateObject | undefined>(undefined);

  const [mapReady, setMapReady] = useState(false);
  const [warehouseName, setWarehouseName] = useState('');
  const [coordinates, setCoordinates] = useState<number[][]>([]);
  const [scheme, setScheme] = useState<Scheme>(Scheme.Single);
  const [marker, setMarker] = useState<Coordinate | undefined>(undefined);
  const [bugsCount, setBugsCount] = useState<number>(0);
  const [bugsVolume, setBugsVolume] = useState<number>(0);
  const [sleeveSize, setSleeveSize] = useState<number>(0);
  const [adjustment, setAdjustment] = useState<IWarehouseAdjustment>({
    x: 0,
    y: 0,
    angle: 0,
  });
  const resetAdjustment = () => setAdjustment({ x: 0, y: 0, angle: 0 });
  const resetAdjustmentPosition = () => setAdjustment({ ...adjustment, x: 0, y: 0 });

  const [hasIntersection, setHasIntersection] = useState(false);
  const [disposition, setDisposition] = useState<DispositionSchema>([]);

  const positions = useStore(warehouseModel.$allSleeveWarehousePositions);
  const loadingWhs = useStore(warehouseModel.$isLoadingAllWarehouses);

  const { center, zoom } = clientStorageService.get(VIEWER_SETTINGS) as {
    center: [y];
    zoom: number;
  };

  // по другому неизвестно как выровнить по центру
  useEffect(() => {
    const dragElement = document.querySelector<HTMLDivElement>('.' + styles.drag);
    if (dragElement && dragElement.style.transform === 'translate(0px, 0px)') {
      dragElement.style.left = `calc(50% - ${dragElement.clientWidth / 2}px)`;
    }
  }, [currentStep]);

  const onSave = useResponseErrorInterceptorHandler({
    handler: async () => {
      const data = isFixedWarehouse
        ? {
            type: WarehouseTypes.Stationary,
            name: warehouseName,
            position: {},
            bags_amount: 10,
            marker: marker,
          }
        : {
            type: WarehouseTypes.Sleeve,
            name: warehouseName,
            position: coordinates,
            square: square,
            adjustment: adjustment,
            bags_size: sleeveSize,
            bags_type: SCHEME2NUMBER_MAP[scheme],
            marker,
            enter: [
              [input?.latitude, input?.longitude],
              [output?.latitude, output?.longitude],
            ],
          };

      // @ts-ignore
      const warehouse = await warehousesService.create(data);

      showInformer({
        title: `Склад успешно создан`,
        status: 'success',
        timeout: 5000,
      });
      onCreate?.(warehouse);

      const query = new URLSearchParams({
        [WAREHOUSES_ID_QUERY_NAME]: warehouse.id.toString(),
        [WAREHOUSES_TYPE_QUERY_NAME]: isFixedWarehouse
          ? WarehouseTypes.Stationary.toString()
          : WarehouseTypes.Sleeve.toString(),
      });
      navigate(`/storage?${query.toString()}`, { replace: true });
    },
  });

  const onCalculate = useResponseErrorInterceptorHandler<
    Parameters<ISelectTypeProps['onCalculate']>
  >({
    handler: async (options) => {
      const result = await warehousesService.calculate({
        size: options.sleeveSize,
        adjustment: options.adjustment,
        type: options.scheme,
        position: coordinates,
        square: square,
      });
      setDisposition(result);
    },
  });

  return (
    <PageTemplate header="Добавление рукавного склада">
      <ButtonWithIcon
        icon={CrossIcon}
        className={styles.close}
        onClick={() => navigate(-1)}
      >
        Закрыть
      </ButtonWithIcon>

      <div className={styles.container}>
        <StepList
          className={styles.steps}
          list={steps}
          currentStep={currentStep}
          availableSteps={availableSteps}
        />

        <CreateMap
          mode={mode}
          zoom={zoom}
          center={center}
          controls={[
            'typeSelector',
            'geolocationControl',
            'fullscreenControl',
            'zoomControl',
            'rulerControl',
          ]}
          restrictedAreas={positions}
          disposition={disposition}
          sleeveLength={sleeveSize}
          type={isFixedWarehouse ? 'fixed' : 'default'}
          onSquareChange={(value) => {
            setSquare(value);
            setSleeveSize(0);
            // resetAdjustment();
          }}
          onIntersectionCheck={setHasIntersection}
          onCloseSetAngle={() => setMode('setAngle')}
          onUpdateTopAngle={setTopAngle}
          onUpdateBottomAngle={setBottomAngle}
          onUpdateInput={(value) => {
            setInput(value);
            // resetAdjustment();
          }}
          onUpdateOutput={(value) => {
            setOutput(value);
            // resetAdjustment();
          }}
          onSendCoordinates={setCoordinates}
          onSetMarker={setMarker}
          onBugsCount={setBugsCount}
          onBugsVolume={setBugsVolume}
          onMapReady={() => {
            setMapReady(true);
            warehouseModel.loadAllWarehouses();
          }}
        />

        <Draggable defaultClassName={styles.drag} bounds="parent">
          <div>
            {[DefaultWarehouseStep.Name, FixedWarehouseStep.Name].includes(
              currentStep,
            ) && (
              <WarehouseName
                canProceed={mapReady && !loadingWhs}
                name={warehouseName}
                onChange={setWarehouseName}
                onNext={(name: string) => {
                  setWarehouseName(name);

                  setAvailableSteps((steps) => [
                    ...steps,
                    isFixedWarehouse
                      ? FixedWarehouseStep.Name
                      : DefaultWarehouseStep.Name,
                  ]);
                  setCurrentStep(
                    isFixedWarehouse
                      ? FixedWarehouseStep.Coordinates
                      : DefaultWarehouseStep.Perimeter,
                  );
                  setMode(isFixedWarehouse ? 'setMarker' : 'setPolygon');
                }}
              />
            )}

            {currentStep === DefaultWarehouseStep.Perimeter &&
              mode === 'setPolygon' && (
                <WarehouseSize
                  size={square}
                  hasIntersection={hasIntersection}
                  onPrev={() => {
                    setAvailableSteps((steps) =>
                      steps.filter((s) => s !== currentStep),
                    );
                    setCurrentStep(DefaultWarehouseStep.Name);
                    setMode('none');
                  }}
                  onNext={() => {
                    setAvailableSteps((steps) => [
                      ...steps,
                      DefaultWarehouseStep.Perimeter,
                    ]);
                    setCurrentStep(DefaultWarehouseStep.Entry);
                    setMode('setInput');
                  }}
                />
              )}

            {currentStep === FixedWarehouseStep.Coordinates && mode === 'setMarker' && (
              <WarehouseCoordinates
                marker={marker}
                hasIntersection={hasIntersection}
                onPrev={() => {
                  setAvailableSteps((steps) => steps.filter((s) => s !== currentStep));
                  setCurrentStep(DefaultWarehouseStep.Name);
                  setMode('none');
                }}
                onNext={onSave}
              />
            )}

            {/*  Angle is not used */}
            {/*            {currentStep === DefaultWarehouseStep.Angle && (
              <TiltAngle
                topPosition={topAngle}
                bottomPosition={bottomAngle}
                onUpdateTopPosition={() => setMode('setTopAngle')}
                onUpdateBottomPosition={() => setMode('setBottomAngle')}
                onNext={() => {
                  setAvailableSteps((steps) => [...steps, DefaultWarehouseStep.Angle]);
                  setCurrentStep(DefaultWarehouseStep.Entry);
                  setMode('setEntry');
                }}
              />
            )}*/}

            {currentStep === DefaultWarehouseStep.Entry && (
              <InputOutput
                topPosition={input}
                bottomPosition={output}
                currentMode={mode}
                onUpdateTopPosition={() => setMode('setInput')}
                onUpdateBottomPosition={() => setMode('setOutput')}
                onPrev={() => {
                  setAvailableSteps((steps) => steps.filter((s) => s !== currentStep));
                  setCurrentStep(
                    isFixedWarehouse
                      ? FixedWarehouseStep.Coordinates
                      : DefaultWarehouseStep.Perimeter,
                  );
                  setMode(isFixedWarehouse ? 'setMarker' : 'setPolygon');
                }}
                onNext={() => {
                  setAvailableSteps((steps) => [...steps, DefaultWarehouseStep.Entry]);
                  setCurrentStep(DefaultWarehouseStep.Type);
                  setMode('setSleeves');
                }}
              />
            )}

            {currentStep === DefaultWarehouseStep.Type && (
              <SelectType
                mapSize={square}
                bugsCount={bugsCount}
                bugsVolume={bugsVolume}
                sleeveSize={sleeveSize}
                adjustment={adjustment}
                scheme={scheme}
                onPrev={() => {
                  setAvailableSteps((steps) => steps.filter((s) => s !== currentStep));
                  setCurrentStep(DefaultWarehouseStep.Entry);
                  setMode('setInput');
                }}
                onNext={onSave}
                onCalculate={onCalculate}
                onUpdateSleeveSize={setSleeveSize}
                onUpdateAdjustment={setAdjustment}
                onUpdateScheme={setScheme}
              />
            )}
          </div>
        </Draggable>
      </div>
    </PageTemplate>
  );
}

export interface CreateWarehousePageProps {
  onCreate?: Function;
}
